
import { computed, onMounted, ref } from 'vue';
import { useAuthStore } from '../stores/auth';
import { storeToRefs } from 'pinia';
import { formatCurrency } from '../utils/currency';
import { useToast } from 'primevue/usetoast';
import { useSession } from '../stores/session';
import { game } from '../apis/game';
import { GamesList, ProviderListResult } from '../types/game';
import { showVuePopup } from '../utils/showVuePopup';
import Login from './Login.vue';
import { openGameUrl } from '../utils/openGameUrl';
import { isHiddenProduct } from '../utils/site-config';

export default {
	name: 'Recently',
	methods: { formatCurrency },
	setup() {
		const auth = useAuthStore();
		auth.hydrate();
		const { profile } = storeToRefs(auth);
		const toast = useToast();
		const session = useSession();

		const loadingGames = ref(false);
		const search = ref('');
		const games = ref<GamesList[]>([]);
		const error = ref('');
		const selectedGame = ref<GamesList | null>(null);

		const gamesList = computed(() => {
			if (!search.value.trim()) return games.value;
			return games.value.filter((g) =>
				g.gameName?.toLowerCase().includes(search.value.toLowerCase())
			);
		});

		const handleRecently = async () => {
			loadingGames.value = true;

			try {
				const resp = await game.recently();
				if (resp.code !== '0') throw new Error(resp.message);
				const result = resp.result as ProviderListResult;
				games.value = result.gamesList.filter(
					(item) => !isHiddenProduct(item)
				);
			} catch (error: any) {
				console.error(error);
				toast.add({
					severity: 'error',
					summary: 'ไม่สามารถโหลดเกมล่าสุด',
					detail: error.message,
					life: 3000,
				});
			} finally {
				loadingGames.value = false;
			}
		};

		const clickBtnFavorite = async (code: string, actionOld: boolean) => {
			try {
				if (actionOld) {
					await handleFavoriteRemove(code);
				} else {
					await handleFavoriteAdd(code);
				}
				// Toggle favorite ใน list ทันที (ไม่โหลดใหม่)
				games.value = games.value.map((g) =>
					g.loginCode === code ? { ...g, isFavorite: !actionOld } : g
				);
			} catch (err: any) {
				error.value = err.message;
			}
		};

		const handleFavoriteAdd = async (code: string) => {
			const resp = await game.favoriteAdd(code);
			if (resp.code !== '0') throw new Error(resp.message);
			toast.add({
				severity: 'success',
				summary: resp.message,
				detail: 'เพิ่มลงรายการโปรดสำเร็จ',
				life: 3000,
			});
		};

		const handleFavoriteRemove = async (code: string) => {
			const resp = await game.favoriteDelete(code);
			if (resp.code !== '0') throw new Error(resp.message);
			toast.add({
				severity: 'success',
				summary: resp.message,
				detail: 'ลบรายการโปรดสำเร็จ',
				life: 3000,
			});
		};

		const clickGame = async (code: string) => {
			if (!auth.isLoggedIn) {
				await showVuePopup(Login);
				return;
			}

			const found = games.value.find((g) => g.loginCode === code);
			if (found) {
				selectedGame.value = found;
				try {
					const payload = {
						gameType: 1,
						productCode: found.productCode,
						code: code,
						redirect: window.location.origin,
						isMobile: true,
					};
					const resp = await game.login(payload);
					if (resp.code !== '0') throw new Error(resp.message);
					openGameUrl(resp?.url || window.location.href, (target) => {
						sessionStorage.setItem('go_to', target)
						window.location.href = '/goto'
					})
				} catch (err: any) {
					error.value = err.message;
					toast.add({
						severity: 'error',
						summary: 'เกิดข้อผิดพลาด',
						detail: err.message,
						life: 3000,
					});
				}
			}
		};

		onMounted(() => {
			handleRecently();
		});

		return {
			profile,
			search,
			gamesList,
			loadingGames,
			clickBtnFavorite,
			clickGame,
		};
	},
};
